import {
  addQuoteProduct,
  createQuote,
  createQuoteSet,
  deleteQuote,
  deleteQuoteProduct,
  deleteSavedQuote,
  generateQuotePdf,
  getQuoteSet,
  getQuoteSetProperty,
  listQuotes,
  listQuoteConversations,
  listQuoteStatus,
  listSavedQuotes,
  loadQuote,
  purchaseEmail,
  saveQuote,
  sendQuotePurchaseOrder,
  updateQuote,
  updateQuoteStatus,
  getQuotePurchaseSettings,
  listQuoteOrganizations,
  updateQuotePurchaseSettings,
  exportQuote,
} from '../thunks/marketplace-quote';
import { handleFulfilled, handlePending, handleRejected } from './utils';

export const marketplaceQuoteReducers = (builder) => {
  // create quote set
  builder
    .addCase(createQuoteSet.fulfilled, (state, action) => {
      state.selectedQuoteSet = action.payload;
      state.newQuoteSetForm.loading = false;
    })
    .addCase(createQuoteSet.pending, (state) => handlePending(state, 'newQuoteSetForm'))
    .addCase(createQuoteSet.rejected, (state) => handleRejected(state, 'newQuoteSetForm'));

  // get quote set
  builder.addCase(getQuoteSet.fulfilled, (state, action) => {
    state.selectedQuoteSet = action.payload;
  });

  // get quote set property
  builder.addCase(getQuoteSetProperty.fulfilled, (state, action) => {
    state.selectedQuoteSetProperty = action.payload;
  });

  // list quotes
  builder
    .addCase(listQuotes.fulfilled, (state, action) => {
      state.quotes.loading = false;
      state.quotes.value = action.payload;
      state.quotes.initialised = true;
    })
    .addCase(listQuotes.pending, (state) => handlePending(state, 'quotes'))
    .addCase(listQuotes.rejected, (state) => handleRejected(state, 'quotes'));

  // list quote organizations
  builder
    .addCase(listQuoteOrganizations.fulfilled, (state, action) => handleFulfilled(state, 'quoteOrganizations', action))
    .addCase(listQuoteOrganizations.pending, (state) => handlePending(state, 'quoteOrganizations'))
    .addCase(listQuoteOrganizations.rejected, (state) => handleRejected(state, 'quoteOrganizations'));

  // create quote
  builder
    .addCase(createQuote.fulfilled, (state, action) => {
      const quotes = [...state.quotes.value];
      const quote = action.payload;
      quotes.unshift({ ...quote, items: [] });
      state.quotes.value = quotes;
      state.newQuoteForm.loading = false;
    })
    .addCase(createQuote.pending, (state) => handlePending(state, 'newQuoteForm'))
    .addCase(createQuote.rejected, (state) => handleRejected(state, 'newQuoteForm'));

  // update quote
  builder
    .addCase(updateQuote.fulfilled, (state, action) => {
      const quotes = [...state.quotes.value];
      const updatedQuote = action.payload;
      const quoteIndex = quotes.findIndex((quote) => quote.id === updatedQuote.id);
      if (quoteIndex >= 0) {
        quotes[quoteIndex].name = updatedQuote?.name;
      }
      state.quotes.value = quotes;
      state.newQuoteForm.loading = false;
    })
    .addCase(updateQuote.pending, (state) => handlePending(state, 'newQuoteForm'))
    .addCase(updateQuote.rejected, (state) => handleRejected(state, 'newQuoteForm'));

  // delete quote
  builder.addCase(deleteQuote.fulfilled, (state, action) => {
    const quotes = [...state.quotes.value];
    const quoteId = action.payload;
    const quoteIndex = quotes.findIndex((quote) => quote.id === quoteId);
    if (quoteIndex >= 0) {
      quotes.splice(quoteIndex, 1);
    }
    state.quotes.value = quotes;
  });

  // add quote product
  builder
    .addCase(addQuoteProduct.fulfilled, (state, action) => {
      const quotes = [...state.quotes.value];
      const addedProduct = action.payload;
      const quoteIndex = quotes.findIndex((quote) => quote.id === addedProduct.quote_id);
      quotes[quoteIndex].items.push(addedProduct);
      state.quotes.value = quotes;
      state.addQuoteProduct.loading = false;
    })
    .addCase(addQuoteProduct.pending, (state) => handlePending(state, 'addQuoteProduct'))
    .addCase(addQuoteProduct.rejected, (state) => handleRejected(state, 'addQuoteProduct'));

  // delete quote product
  builder.addCase(deleteQuoteProduct.fulfilled, (state, action) => {
    const quotes = [...state.quotes.value];
    const { quoteId, itemId } = action.payload;
    const quoteIndex = quotes.findIndex((quote) => quote.id === quoteId);
    if (quoteIndex >= 0) {
      const quote = quotes[quoteIndex];
      const items = [...quote.items] || [];
      const itemIndex = items.findIndex((item) => item.id === itemId);
      if (itemIndex >= 0) {
        items.splice(itemIndex, 1);
        quotes[quoteIndex].items = items;
      }
    }
    state.quotes.value = quotes;
  });

  //generate quote
  builder
    .addCase(generateQuotePdf.fulfilled, (state) => {
      state.generateQuoteForm.loading = false;
    })
    .addCase(generateQuotePdf.pending, (state) => handlePending(state, 'generateQuoteForm'))
    .addCase(generateQuotePdf.rejected, (state) => handleRejected(state, 'generateQuoteForm'));

  // purchase settings
  builder.addCase(getQuotePurchaseSettings.fulfilled, (state, action) => {
    state.generateQuoteForm = { ...state.generateQuoteForm, ...action.payload };
    state.generateQuoteForm.loading = false;
  });
  builder.addCase(updateQuotePurchaseSettings.fulfilled, (state, action) => {
    const { quoteId, orgId } = action.payload;
    const quotes = [...state.quotes.value];
    const quoteIndex = quotes.findIndex((quote) => quote.id === quoteId);
    quotes[quoteIndex].organisation_id = orgId;
    state.quotes.value = quotes;
  });

  // purchase email
  builder.addCase(purchaseEmail.fulfilled, (state, action) => {
    state.purchaseEmailData = action.payload;
  });

  // send purchase order email
  builder
    .addCase(sendQuotePurchaseOrder.fulfilled, (state, action) => {
      state.sendPurchaseOrderForm.loading = false;
      const payload = action.payload;
      const quote = state.quotes.value.find((q) => q.id === payload.quote_id);
      if (quote) {
        quote.items.forEach((item) => {
          if (payload.item_ids.includes(item.id)) {
            item.purchase_order_number = payload.purchase_order_number;
          }
        });
      }
    })
    .addCase(sendQuotePurchaseOrder.pending, (state) => handlePending(state, 'sendPurchaseOrderForm'))
    .addCase(sendQuotePurchaseOrder.rejected, (state) => handleRejected(state, 'sendPurchaseOrderForm'));

  // update quote status
  builder.addCase(updateQuoteStatus.fulfilled, (state, action) => {
    const quotes = [...state.quotes.value];
    const updatedQuote = action.payload;
    const quoteIndex = quotes.findIndex((quote) => quote.id === updatedQuote.id);
    if (quoteIndex >= 0) {
      quotes[quoteIndex].status = updatedQuote?.status;
      quotes[quoteIndex].invoice_number = updatedQuote?.invoice_number;
    }
    state.quotes.value = quotes;
  });

  //save quote
  builder
    .addCase(saveQuote.fulfilled, (state) => {
      state.saveQuoteForm.loading = false;
    })
    .addCase(saveQuote.pending, (state) => handlePending(state, 'saveQuoteForm'))
    .addCase(saveQuote.rejected, (state) => handleRejected(state, 'saveQuoteForm'));

  // list saved quotes
  builder
    .addCase(listSavedQuotes.fulfilled, (state, action) => {
      state.savedQuotes.loading = false;
      state.savedQuotes.value = action.payload;
    })
    .addCase(listSavedQuotes.pending, (state) => handlePending(state, 'savedQuotes'))
    .addCase(listSavedQuotes.rejected, (state) => handleRejected(state, 'savedQuotes'));

  //delete saved quote
  builder
    .addCase(deleteSavedQuote.fulfilled, (state) => {
      state.savedQuotes.loading = false;
    })
    .addCase(deleteSavedQuote.pending, (state) => handlePending(state, 'savedQuotes'))
    .addCase(deleteSavedQuote.rejected, (state) => handleRejected(state, 'savedQuotes'));

  // load quote
  builder
    .addCase(loadQuote.fulfilled, (state, action) => {
      const quotes = [...state.quotes.value];
      const quote = action.payload;
      quotes.push(quote);
      state.quotes.value = quotes;
      state.loadQuoteForm.loading = false;
    })
    .addCase(loadQuote.pending, (state) => handlePending(state, 'loadQuoteForm'))
    .addCase(loadQuote.rejected, (state) => handleRejected(state, 'loadQuoteForm'));

  //quote conversations
  builder
    .addCase(listQuoteConversations.fulfilled, (state, action) => {
      const updated = [...state.quoteConversations.value, ...action.payload];
      const uniqueArr = Array.from(new Map(updated.map((convo) => [convo.id, convo])).values());
      state.quoteConversations.value = uniqueArr;
      state.quoteConversations.loading = false;
    })
    .addCase(listQuoteConversations.pending, (state) => handlePending(state, 'quoteConversations'))
    .addCase(listQuoteConversations.rejected, (state) => handleRejected(state, 'quoteConversations'));

  // quote status enums
  builder
    .addCase(listQuoteStatus.fulfilled, (state, action) => handleFulfilled(state, 'quoteStatus', action))
    .addCase(listQuoteStatus.pending, (state) => handlePending(state, 'quoteStatus'))
    .addCase(listQuoteStatus.rejected, (state) => handleRejected(state, 'quoteStatus'));

  // export quote
  builder
    .addCase(exportQuote.fulfilled, (state, action) => {
      const { download } = action.meta.arg;

      if (download) {
        state.quoteExportDownloadLoading = false;
      } else {
        state.quoteExportLoading = false;
      }
    })
    .addCase(exportQuote.pending, (state, action) => {
      const { download } = action.meta.arg;

      if (download) {
        state.quoteExportDownloadLoading = true;
      } else {
        state.quoteExportLoading = true;
      }
    })
    .addCase(exportQuote.rejected, (state, action) => {
      const { download } = action.meta.arg;

      if (download) {
        state.quoteExportDownloadLoading = false;
      } else {
        state.quoteExportLoading = false;
      }
    });
};
